import type { ListParams, ListResponse, ProductDetail } from "./products";

import {
  computeRange,
  computeStock,
  ensureId,
  matchesProductStatusFilter,
  toRow,
} from "./products.helpers";
import { translateUi } from "@i18n/config";

const KEY = "storizone:products:v2";

export function loadLocal(): ProductDetail[] {
  if (typeof window === "undefined") return [];
  const raw = window.localStorage.getItem(KEY);
  if (raw) return JSON.parse(raw) as ProductDetail[];
  const seed: ProductDetail[] = Array.from({ length: 12 }).map((_, i) => {
    const id = `p_${i + 1}`;
    const price = 19 + i * 3;
    const salePrice = i % 3 === 0 ? price - 5 : null;
    const base: ProductDetail = {
      id,
      name: `Product ${i + 1}`,
      description: "Sample product description",
      slug: `product-${i + 1}`,
      sku: `SKU-${i + 1}`,
      price,
      salePrice,
      currency: "USD",
      priceMin: salePrice ?? price,
      priceMax: price,
      storefrontSortOrder: (i + 1) * 1024,
      status: i % 4 === 0 ? "inactive" : "active",
      createdAt: new Date(Date.now() - i * 86400000).toISOString(),
      updatedAt: new Date(Date.now() - i * 3600000).toISOString(),
      stockQty: 20 + i,
      trackInventory: true,
      brandId: i % 2 === 0 ? "b_apple" : "b_samsung",
      categoryIds: i % 2 === 0 ? ["cat_elec"] : ["cat_home"],
      labelIds: i % 3 === 0 ? ["l_sale"] : [],
      compareAt: salePrice ? price : null,
      images: [
        {
          id: ensureId("img"),
          url: `https://picsum.photos/seed/p${i + 1}/200/200`,
          alt: translateUi("productValue0", "Product {{value0}}", {
            value0: i + 1,
          }),
          sort: 0,
          isPrimary: true,
        },
      ],
      variants: [],
      productType: 0,
      categoryCount: 0,
      weight: null,
    };
    return base;
  });
  window.localStorage.setItem(KEY, JSON.stringify(seed));
  return seed;
}

export function persistLocal(next: ProductDetail[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(KEY, JSON.stringify(next));
}

export function listProductsFromStub(
  params: ListParams,
  page: number,
  pageSize: number,
): ListResponse {
  const list = loadLocal();
  const q = (params.q || "").toLowerCase().trim();
  let rows = list.filter((p) => {
    if (
      params.status === "scheduled"
        ? !(p.status === "inactive" && p.activateAtUtc)
        : params.status && !matchesProductStatusFilter(p.status, params.status)
    )
      return false;
    if (params.brandId && p.brandId !== params.brandId) return false;
    if (params.categoryId && !(p.categoryIds || []).includes(params.categoryId))
      return false;
    if (
      q &&
      !(
        p.name.toLowerCase().includes(q) ||
        (p.slug || "").toLowerCase().includes(q) ||
        p.sku.toLowerCase().includes(q)
      )
    )
      return false;
    if (params.stock === "out" && computeStock(p) > 0) return false;
    if (params.stock === "in" && computeStock(p) <= 0) return false;
    if (params.stock === "low" && computeStock(p) > 5) return false;
    return true;
  });

  const sortKey = params.sort ?? "updatedAt";
  const dir = params.sortDir ?? "desc";
  rows = rows.sort((a, b) => {
    let va: any = "";
    let vb: any = "";
    if (sortKey === "storefront")
      return (a.storefrontSortOrder ?? 0) - (b.storefrontSortOrder ?? 0);
    if (sortKey === "name") {
      va = a.name;
      vb = b.name;
    } else if (sortKey === "price") {
      va = computeRange(a).min;
      vb = computeRange(b).min;
    } else if (sortKey === "createdAt") {
      va = a.createdAt;
      vb = b.createdAt;
    } else {
      va = a.updatedAt;
      vb = b.updatedAt;
    }
    if (va < vb) return dir === "asc" ? -1 : 1;
    if (va > vb) return dir === "asc" ? 1 : -1;
    return 0;
  });

  const total = rows.length;
  const start = (page - 1) * pageSize;
  const items = rows.slice(start, start + pageSize).map(toRow);
  return { items, total, page, pageSize };
}
